import { useEffect, useMemo, useState, useRef } from 'react'
import { IoMdClose } from 'react-icons/io'
import { MdKeyboardArrowDown, MdLocationOn } from 'react-icons/md'

import { Option } from 'src/types/form'

import { StandaloneCheckbox } from '../StandaloneCheckbox'

type MultiSelectProps = {
  id: string
  placeHolder?: string
  disabled?: boolean
  className?: string
  onChange?: (values: (string | undefined)[]) => void
  options?: Option[]
  value: string[]
  columns?: number
}

const GRID_COLS: Record<number, string> = {
  2: 'grid grid-cols-[repeat(2,max-content)] gap-x-6 gap-y-1',
  3: 'grid grid-cols-[repeat(3,max-content)] gap-x-6 gap-y-1',
  4: 'grid grid-cols-[repeat(4,max-content)] gap-x-6 gap-y-1'
}

export const MultiSelect = ({
  id,
  disabled = false,
  placeHolder = 'Please select',
  className = '',
  onChange,
  value,
  options = [],
  columns = 1
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelectedValues, setTempSelectedValues] = useState<
    (string | undefined)[]
  >(value || [])
  const [appliedValues, setAppliedValues] = useState<(string | undefined)[]>(
    value || []
  )
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedOptions = useMemo(() => {
    return options.filter(option => appliedValues.includes(option.value))
  }, [options, appliedValues])

  // Sync with external value prop changes
  useEffect(() => {
    setAppliedValues(value || [])
    setTempSelectedValues(value || [])
  }, [value])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        // Reset temp values to applied values when closing without applying
        setTempSelectedValues(appliedValues)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, appliedValues])

  const handleRemoveChip = (
    valueToRemove: string | undefined,
    e: React.MouseEvent
  ) => {
    e.stopPropagation()
    const newValues = appliedValues.filter(v => v !== valueToRemove)
    setAppliedValues(newValues)
    setTempSelectedValues(newValues)
    if (onChange) {
      onChange(newValues)
    }
  }

  const handleOptionSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: eventValue } = e.target
    setTempSelectedValues(current => {
      if (current.includes(eventValue)) {
        return current.filter(v => v !== eventValue)
      }
      return [...current, eventValue]
    })
  }

  const handleApply = () => {
    setAppliedValues(tempSelectedValues)
    setIsOpen(false)
    if (onChange) {
      onChange(tempSelectedValues)
    }
  }

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        // Opening dropdown - sync temp values with current applied values
        setTempSelectedValues(appliedValues)
      }
      setIsOpen(p => !p)
    }
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type='button'
        onClick={handleToggleDropdown}
        className='inline-flex items-center gap-2 px-4 h-15 rounded-lg border border-forestDeep/30 bg-white text-left text-base font-body-rebrand text-forestDeep focus:outline-none min-w-[250px] w-full justify-between'
        disabled={disabled}
      >
        <div className='flex items-center gap-2 flex-1 min-w-0'>
          <MdLocationOn className='text-xl shrink-0 text-forestDeep' />
          {selectedOptions.length === 0 ? (
            <span className='text-forestDeep/70'>{placeHolder}</span>
          ) : (
            <div className='flex flex-wrap gap-1 flex-1'>
              {selectedOptions.map(option => (
                <span
                  key={option.value}
                  className='inline-flex items-center gap-1 px-2 py-0.5 text-sm rounded-md bg-limeBright text-forestDeep'
                  role='presentation'
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                >
                  {option.label}
                  <button
                    type='button'
                    onClick={e => handleRemoveChip(option.value, e)}
                    className='rounded-full p-0.5 transition-colors hover:bg-forestDeep/20'
                  >
                    <IoMdClose className='text-sm' />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {disabled ? null : (
          <MdKeyboardArrowDown
            className={`w-6 h-6 shrink-0 text-forestDeep transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div className='absolute z-50 mt-2 min-w-[256px] w-max max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-slate-200 max-h-[420px] overflow-y-auto'>
          <div
            className={`p-2 ${
              columns > 1
                ? GRID_COLS[columns] ?? 'grid grid-cols-3 gap-1'
                : 'flex flex-col'
            }`}
          >
            {options.map((o, idx) => {
              return (
                <label
                  key={idx}
                  className='flex items-center gap-2 px-2 py-1 hover:bg-slate-100 rounded-sm cursor-pointer transition-colors [&_span]:focus-within:shadow-none'
                >
                  <StandaloneCheckbox
                    id={`${id}-${o.value}`}
                    value={o.value}
                    onChange={handleOptionSelect}
                    checked={tempSelectedValues.includes(o.value)}
                  />
                  <span className='text-sm text-slate-700 whitespace-nowrap'>
                    {o.label}
                  </span>
                </label>
              )
            })}
          </div>
          <div className='border-t border-slate-200 p-3'>
            <button
              type='button'
              onClick={handleApply}
              className='w-full px-4 py-2 rounded-lg font-medium transition-colors bg-limeBright text-forestDeep hover:bg-limeBrightHover'
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
